import { morph } from '@theme/morph';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);

      morphSection(sectionId, sectionHTML);
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl).then((response) => {
      return response.text();
    });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    const sectionHTML = await pendingPromise;
    this.#pendingPromises.delete(sectionUrl);

    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/** @type {ReturnType<typeof setTimeout> | undefined} */
let debouncedCartJsonTimer;

const CART_SELECTORS =
  'cart-drawer-component, cart-items-component, .cart-drawer__dialog, .cart-drawer, form.cart__contents-form, form[action*="/cart"], .cart__summary';

/** @returns {HTMLElement} */
function resolveCartCompatRoot(scope) {
  const body = document.body;

  if (scope instanceof Element) {
    if (scope.matches(CART_SELECTORS) || !!scope.querySelector?.(CART_SELECTORS)) {
      return scope;
    }

    /** @type {HTMLElement | null} */
    const header = document.getElementById('header-group');
    if (header && (header.matches(CART_SELECTORS) || header.querySelector(CART_SELECTORS))) {
      return header;
    }

    const drawerFromDoc = /** @type {HTMLElement | null} */ (document.querySelector('cart-drawer-component'));
    if (drawerFromDoc) return drawerFromDoc;
  }

  if (scope instanceof Document) {
    return scope.documentElement;
  }

  return body;
}

/** @returns {boolean} */
function pageHasCartUI() {
  return !!document.body?.querySelector(CART_SELECTORS);
}

/** @param {HTMLElement} logicalRoot */
function dispatchCodSyntheticEvents(logicalRoot) {
  const bubbleOpts = { bubbles: true };
  /** @type {EventTarget[]} */
  const recipients = /** @type {EventTarget[]} */ ([logicalRoot, document, window]);

  const drawer = document.querySelector('cart-drawer-component');
  if (drawer instanceof HTMLElement) recipients.push(drawer);

  const summary = /** @type {HTMLElement | null} */ (
    document.querySelector('.cart-drawer__summary, .cart__summary, cart-items-component')
  );
  if (summary) recipients.push(summary);

  const appMount = document.querySelector('#shopify-cart-app-extra-buttons');
  if (appMount instanceof HTMLElement) recipients.push(appMount);

  /** @type {string[]} */
  const types = ['cart:updated', 'shopify-cart:updated', 'ajaxCart:afterCartLoaded'];

  recipients.forEach((target) => {
    if (!target) return;
    if (typeof target.dispatchEvent !== 'function') return;
    types.forEach((type) => {
      try {
        /** @type {EventTarget & { dispatchEvent: (e: Event) => boolean }} */ (target).dispatchEvent(
          new CustomEvent(type, bubbleOpts)
        );
      } catch (_) {
        // no-op — some targets reject unknown event types on old hosts
      }
    });
  });

  try {
    window.dispatchEvent(new CustomEvent('reloadCart', bubbleOpts));
  } catch (_) {
    // no-op
  }

  try {
    document.dispatchEvent(
      new CustomEvent('cart:morph-complete', {
        bubbles: true,
        detail: {
          appButtonMountSelector: '#shopify-cart-app-extra-buttons',
          checkoutSelector: '#checkout',
          checkoutCtasSelector: '.cart__ctas',
        },
      })
    );
  } catch (_) {
    // no-op
  }
}

/** @returns {void} */
function scheduleDebouncedCartJsonFetch() {
  clearTimeout(debouncedCartJsonTimer);
  debouncedCartJsonTimer = setTimeout(() => {
    debouncedCartJsonTimer = undefined;
    const root =
      typeof Theme !== 'undefined' && Theme.routes?.cart_url
        ? Theme.routes.cart_url.replace(/\/$/, '')
        : '';
    const cartJsonUrl = root ? `${root}.js` : '/cart.js';

    fetch(cartJsonUrl, { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((cart) => {
        const withDetail = { bubbles: true, detail: { cart } };
        document.dispatchEvent(new CustomEvent('cart:updated', withDetail));
        window.dispatchEvent(new CustomEvent('cart:refresh', withDetail));
      })
      .catch(() => {});
  }, 135);
}

/**
 * COD / checkout apps inject client-side markup that this theme wipes on every AJAX cart morph.
 * Fire synthetic hooks several times — many scripts bind after morph or only on first response.
 *
 * Never dispatch Theme `cart:update` here (would break CartIcon counts).
 *
 * @param {Element | Document | undefined} scope - Prefer the morphed `.shopify-section` or `#header-group`
 */
export function notifyThirdPartyCartScripts(scope) {
  if (typeof document === 'undefined') return;

  const body = document.body;
  if (!body || !pageHasCartUI()) return;

  const logicalRoot = resolveCartCompatRoot(scope ?? body);

  const fire = () => dispatchCodSyntheticEvents(logicalRoot);

  queueMicrotask(fire);

  requestAnimationFrame(() => {
    queueMicrotask(fire);
    requestAnimationFrame(() => queueMicrotask(fire));
  });

  /** @type {number[]} */
  const delaysMs = [20, 60, 160, 320, 600];
  delaysMs.forEach((ms) => setTimeout(() => queueMicrotask(fire), ms));

  scheduleDebouncedCartJsonFetch();
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 */
export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement);

  notifyThirdPartyCartScripts(existingElement);
  notifyThirdPartyCartScripts(document.body);
}

export const sectionRenderer = new SectionRenderer();
